<template>
  <div id="verbatimTable">
    <el-table
      :data="sortedData"
      style="width: 100%;"
      :class="`tableWrap pulse${pulseType}`"
      :show-header="false"
      :empty-text="'No data to display'"
      :span-method="objectSpanMethod"
      :border="true"
      :cell-style="{borderColor: '#bcbcbc'}"
    >
      <el-table-column
        prop="role"
        width="180"
      >
        <template slot-scope="scope">
          <div class="rowRole">
            {{ scope.row.role }}
          </div>
          <div class="rowType">
            {{ scope.row.divide }}
          </div>
        </template>
      </el-table-column>

      <el-table-column
        prop="comment"
      >
        <template slot-scope="scope">
          <div
            v-if="scope.row.censored"
            class="rowCensored"
          >
            {{ scope.row.comment }}
          </div>
          <div v-else>
            {{ scope.row.comment }}
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
export default {
  props: {
    scene: {
      type: String,
      default: '',
    },

    report: {
      type: Object,
      default: null,
    },

    pulseType: {
      type: Number,
      default: 1,
    },
  },

  computed: {
    dataInit() {
      const list = [];

      // Pulse data
      Object.keys(this.report).forEach((pulse) => {
        const short = this.report[pulse][this.scene];
        if (short.stakeholder) {
          short.coworker = short.stakeholder;
          delete short.stakeholder;
        }

        Object.keys(short).forEach((key) => {
          let unit = {};

          if (short[key] !== 'CENSORED') {
            // Not censored data
            if (key === 'self') {
              unit = {
                role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
                comment: short[key],
                divide: `Pulse ${pulse === 'pulse1' ? 'I' : 'II'}`,
              };
              list.push(unit);
            } else {
              short[key].forEach((item) => {
                unit = {
                  role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
                  comment: item,
                  divide: `Pulse ${pulse === 'pulse1' ? 'I' : 'II'}`,
                };
                list.push(unit);
              });
            }
          } else {
            // Censored data
            unit = {
              role: `${key.charAt(0).toUpperCase() + key.slice(1)}`,
              comment: 'Not enough responses to display',
              divide: `Pulse ${pulse === 'pulse1' ? 'I' : 'II'}`,
              censored: 'yes',
            };
            list.push(unit);
          }
        });
      });

      return list;
    },

    sortedData() {
      const sorted = [];
      const roleOrder = ['Self', 'Manager', 'Coworker'];
      const pulseOrder = ['Pulse I', 'Pulse II'];

      if (this.dataInit && this.dataInit.length) {
        const list = this.dataInit;

        roleOrder.forEach((role) => {
          pulseOrder.forEach((pulse) => {
            list.reduce((stack, current) => {
              if (current.role === role) {
                if (current.divide === pulse) {
                  stack.push(current);
                }
              }
              return stack;
            }, sorted);
          });
        });
      }

      return sorted;
    },

    getFilter() {
      const filter = {
        pulse1: {
          Self: {
            flag: true,
            first: 0,
            last: 0,
          },
          Manager: {
            flag: true,
            first: 0,
            last: 0,
          },
          Coworker: {
            flag: true,
            first: 0,
            last: 0,
          },
        },
        pulse2: {
          Self: {
            flag: true,
            first: 0,
            last: 0,
          },
          Manager: {
            flag: true,
            first: 0,
            last: 0,
          },
          Coworker: {
            flag: true,
            first: 0,
            last: 0,
          },
        },
      };
      const list = this.sortedData;
      const range = [];

      // Calculate first, last index each of data
      if (list && list.length) {
        list.forEach((item, index) => {
          if (filter[`pulse${item.divide === 'Pulse I' ? '1' : '2'}`][item.role].flag) {
            filter[`pulse${item.divide === 'Pulse I' ? '1' : '2'}`][item.role].first = index;
            filter[`pulse${item.divide === 'Pulse I' ? '1' : '2'}`][item.role].flag = false;
          } else {
            filter[`pulse${item.divide === 'Pulse I' ? '1' : '2'}`][item.role].last = index;
          }
        });
      }

      // Filter data what actual bending needed
      Object.keys(filter).forEach((pulse) => {
        Object.keys(filter[pulse]).forEach((role) => {
          if (!filter[pulse][role].flag
            && filter[pulse][role].first !== filter[pulse][role].last
            && filter[pulse][role].first < filter[pulse][role].last) {
            range.push([filter[pulse][role].first, filter[pulse][role].last]);
          }
        });
      });

      return range;
    },
  },

  methods: {
    objectSpanMethod({
      rowIndex,
      columnIndex,
    }) {
      const span = {
        rowspan: 1,
        colspan: 1,
      };

      this.getFilter.forEach((range) => {
        if (rowIndex >= range[0] && range[0] !== range[1]) {
          if (columnIndex === 0) {
            if (rowIndex === range[0]) {
              span.rowspan = (range[1] - range[0]) + 1;
              span.colspan = 1;
            } else if (range[0] < rowIndex && rowIndex <= range[1]) {
              span.rowspan = 0;
              span.colspan = 0;
            } else {
              span.rowspan = 1;
              span.colspan = 1;
            }
          } else {
            span.rowspan = 1;
            span.colspan = 1;
          }
        }
      });

      return span;
    },
  },
};
</script>

<style lang="scss" scoped>
  #verbatimTable {
    margin-top: 15px;

    .tableWrap {
      .el-table__row {
        .rowRole {
          font-weight: bold;
          text-align: center;
        }

        .rowType {
          text-align: center;
        }

        .rowCensored {
          text-align: center;
          color: #c4c4c4;
          font-style: italic;
        }
      }

      &.pulse1 {
        border-top: 4px solid #00ace2;
      }

      &.pulse2 {
        border-top: 4px solid #02be90;
      }
    }
  }
</style>

<style lang="scss">
  #verbatimTable {
    .tableWrap {
      .el-table__row {
        background: rgba(196, 196, 196, 0.2);
      }
    }
  }
</style>
